/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/11/22, 7:56 AM
 *
 */

import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { WarningOctagon } from 'phosphor-react';
import { useAppSelector } from 'stores';
import { Member } from 'types/MemberTypes';
import { MemberDetailCategory, MemberDetails, Role } from 'types/RoleTypes';
import { getDirtyFields } from 'utils/getDirtyField';

import { addDetailsToMember } from 'features/members/api/requests/member.request';

import { Switch } from '@sunyahealthnepal/react-ui-components';
import { alert } from 'components/Alert';
import Button from 'components/Button';
import Input from 'components/Input';
import { Modal } from 'components/Modal/useModal';

type MemberDetailAddModalProps = {
  otherDetails: MemberDetails[];
  memberData: Member;
  children?: React.ReactNode;
  selectedRole: Role;
};

const MemberOtherDetailModal: React.FC<MemberDetailAddModalProps> = ({
  memberData,
  children,
  selectedRole,
  otherDetails: selectedMemberDetails,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields, isDirty },
  } = useForm();
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    reset(
      Object.assign(
        {},
        ...selectedMemberDetails.map((element: any) => ({
          [`${element.detail_category_id}-${element.slug}`]: element.value,
        }))
      )
    );
    return () => reset();
  }, [JSON.stringify(selectedMemberDetails)]);

  return (
    <Modal>
      <Modal.Button type="open">
        {children ?? (
          <div className="cursor-pointer    p-6 text-xl font-medium text-gray-500 hover:text-gray-850 hover:text-gray-800">
            {selectedMemberDetails.length !== 0 ? 'Update' : 'Add'} Other Details
          </div>
        )}
      </Modal.Button>
      <Modal.Content>
        <Modal.Title>
          {selectedMemberDetails.length !== 0 ? 'Update' : 'Add'} Member Details {memberData.name}
          &apos;s Details
        </Modal.Title>
        {selectedRole &&
        (!selectedRole.member_detail_categories ||
          selectedRole.member_detail_categories.length === 0) ? (
          <div className="flex items-center space-x-4 text-red-500">
            <WarningOctagon size={40} />{' '}
            <span className="text-xl font-medium">
              No Member Details Field Found. Please contact Administrator
            </span>
          </div>
        ) : (
          <Modal.Form
            onSubmit={handleSubmit(
              async (values) =>
                await alert({
                  type: 'promise',
                  promise: addDetailsToMember(
                    Number(selectedRole.id),
                    Number(memberData.id),
                    getDirtyFields(dirtyFields, values)
                  ),
                  msgs: {
                    loading: 'Adding Member Details',
                  },
                  id: 'member-detail-add',
                })
            )}
          >
            <Modal.Scrollable>
              <div className="space-y-4">
                {selectedRole &&
                  selectedRole.member_detail_categories &&
                  selectedRole.member_detail_categories.map((category: MemberDetailCategory) => (
                    <Fragment key={category.id}>
                      {category.value_type?.toLowerCase() === 'boolean' ? (
                        <Switch
                          name={`${category.id}-${category.slug}`}
                          control={control}
                          label={category.name}
                        />
                      ) : (
                        <Input
                          label={category.name}
                          type={category.value_type}
                          required={!!category.required}
                          placeholder={`Enter ${category.name}`}
                          {...register(`${category.id}-${category.slug}`)}
                        />
                      )}
                    </Fragment>
                  ))}
              </div>
            </Modal.Scrollable>{' '}
            <Button disabled={!isDirty}>Update Details</Button>
          </Modal.Form>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default MemberOtherDetailModal;
